import { ApiError } from '@/lib/api/client';

// A deploy is back inside a minute. A "restarting" reading older than this is
// an outage or this browser's own network, and the ordinary line-health
// handling is the honest answer again.
export const SERVER_RESTART_GIVE_UP_MS = 180_000;

export interface ServerRestart {
  sinceMs: number;
  // The pool this tab held (or wanted) going into the restart.
  campaignId: string;
}

export type ServerRestartPoll =
  | {
      kind: 'response';
      restarting: boolean;
      membershipCampaignId: string | null;
      handoffCampaignId: string | null;
    }
  | { kind: 'failure'; error: unknown };

export interface ServerRestartObservation {
  nowMs: number;
  // The latest membership poll: the server's answer, or the failure that
  // stood in for one.
  poll: ServerRestartPoll;
  // The seat this tab was showing when the poll went out (a failed poll
  // leaves the last answer in place, so this is still set during the outage).
  memberCampaignId: string | null;
  // The rejoin intent: null after an explicit Leave (or a tripped line
  // breaker), undefined when this page load never expressed one.
  intentCampaignId: string | null | undefined;
  // Whether a server handoff may arm a rejoin here at all (agent role, not
  // impersonating).
  canArm: boolean;
}

export interface ServerRestartStep {
  restart: ServerRestart | null;
  // Campaign to arm the rejoin intent for, from the server's handoff record.
  armCampaignId: string | null;
  // Handoff to consume with a Leave instead: this tab left on purpose, and
  // the server's record must not bring the seat back here or anywhere else.
  consumeHandoffCampaignId: string | null;
  // A seat is owed after this observation: confirm once the rejoin lands.
  expectRejoin: boolean;
}

// What a membership poll sees while the backend is recreated behind a proxy
// that stays up: 502/503/504. No answer at all (fetch rejects with a
// TypeError) means the proxy itself is unreachable — this browser's own
// network — so it may keep a restart reading alive but never start one.
export function isServerRestartError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 502 || error.status === 503 || error.status === 504);
}

const NOTHING: Omit<ServerRestartStep, 'restart'> = { armCampaignId: null, consumeHandoffCampaignId: null, expectRejoin: false };

function stepFailure(current: ServerRestart | null, error: unknown, held: string | null, nowMs: number): ServerRestartStep {
  if (current) {
    return { ...NOTHING, restart: nowMs - current.sinceMs >= SERVER_RESTART_GIVE_UP_MS ? null : current };
  }
  const restart = held !== null && isServerRestartError(error) ? { sinceMs: nowMs, campaignId: held } : null;
  return { ...NOTHING, restart };
}

// An answer with no seat and no drain flag: the restart (if any) is over, and
// the server's handoff record is settled here.
function settleSeatless(
  current: ServerRestart | null,
  observation: ServerRestartObservation,
  handoffCampaignId: string | null,
  held: string | null
): ServerRestartStep {
  // A handoff arms only a tab with no opinion yet. An intent that already
  // names this pool needs nothing; one cleared on purpose (Leave, or a phone
  // the breaker gave up on) consumes the record instead, or a later reload
  // with no intent of its own would take the seat back.
  const armCampaignId = observation.canArm && observation.intentCampaignId === undefined ? handoffCampaignId : null;
  const consumeHandoffCampaignId = observation.intentCampaignId === null ? handoffCampaignId : null;
  return {
    restart: null,
    armCampaignId,
    consumeHandoffCampaignId,
    expectRejoin: armCampaignId !== null || (current !== null && held !== null),
  };
}

/**
 * One membership-poll observation against the current restart reading.
 * Enters on the server's own flag or on a restart-shaped failure — only
 * while a seat (or intent) is held, since the reading promises a rejoin;
 * holds through further failures until the give-up window; ends on any
 * answer without the flag.
 */
export function stepServerRestart(current: ServerRestart | null, observation: ServerRestartObservation): ServerRestartStep {
  const { poll, nowMs } = observation;
  const held = observation.memberCampaignId ?? observation.intentCampaignId ?? null;
  if (poll.kind === 'failure') return stepFailure(current, poll.error, held, nowMs);
  if (poll.restarting) {
    return { ...NOTHING, restart: current ?? (held !== null ? { sinceMs: nowMs, campaignId: held } : null) };
  }
  if (poll.membershipCampaignId !== null) return { ...NOTHING, restart: null };
  return settleSeatless(current, observation, poll.handoffCampaignId, held);
}
